import { createServerFn } from "@tanstack/react-start";

export type SustainabilityCard = {
  id: string;
  icon: string;
  title: string;
  text: string;
  visible?: boolean;
};

export type PublicSustainabilityPage = {
  title: string;
  intro: string;
  body: string;
  heroUrl: string | null;
  heroAlt: string | null;
  seoTitle: string;
  seoDescription: string;
  ogDescription: string;
  ctaPrimaryLabel: string;
  ctaPrimaryUrl: string;
  ctaSecondaryLabel: string;
  ctaSecondaryUrl: string;
  cards: SustainabilityCard[];
};

const EMPTY: PublicSustainabilityPage = {
  title: "Zo groen mogelijk",
  intro: "",
  body: "",
  heroUrl: null,
  heroAlt: null,
  seoTitle: "Duurzaamheid — Openluchtfilmfestival Apeldoorn",
  seoDescription: "",
  ogDescription: "",
  ctaPrimaryLabel: "",
  ctaPrimaryUrl: "",
  ctaSecondaryLabel: "",
  ctaSecondaryUrl: "",
  cards: [],
};

async function signPath(path: string | null): Promise<string | null> {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin.storage
    .from("page-media")
    .createSignedUrl(path, 60 * 60 * 24 * 7);
  return data?.signedUrl ?? null;
}

export const getSustainabilityPage = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicSustainabilityPage> => {
    const { cached } = await import("@/lib/server-cache.server");
    return cached("sustainability", 300_000, async () => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data } = await supabaseAdmin
      .from("sustainability_page")
      .select("*")
      .eq("id", "default")
      .maybeSingle();

    if (!data) return EMPTY;

    const cards = ((data.cards as SustainabilityCard[] | null) ?? []).filter(
      (c) => c.visible !== false,
    );

    return {
      title: data.title ?? EMPTY.title,
      intro: data.intro ?? "",
      body: data.body ?? "",
      heroUrl: await signPath((data.hero_path as string | null) ?? null),
      heroAlt: (data.hero_alt as string | null) ?? null,
      seoTitle: data.seo_title || EMPTY.seoTitle,
      seoDescription: data.seo_description ?? "",
      ogDescription: data.og_description ?? "",
      ctaPrimaryLabel: data.cta_primary_label ?? "",
      ctaPrimaryUrl: data.cta_primary_url ?? "",
      ctaSecondaryLabel: data.cta_secondary_label ?? "",
      ctaSecondaryUrl: data.cta_secondary_url ?? "",
      cards,
    };
    });
  },
);